import React, { useCallback, useEffect, useState } from "react";
import * as styles from "./MainMenu.module.scss";
import { RemoveScroll as ReactRemoveScroll } from "react-remove-scroll";
import { MenuSection } from "./MenuSection";
import { ValidatedTopMenuItem } from "../utils";
import { AlternateLink } from "src/utils/localization/alternateLinks";
import {
  CustomMenuItemFragment,
  DefaultNavBarQuery
} from "types/graphql-types";
import { arrayIsEmpty, isPresent } from "src/utils/checks";
import clsx from "clsx";
import MenuSectionSingle from "./MenuSectionSingle";
import { globalHistory } from "@reach/router";
import HamburgeButton from "../HamburgerButton/HamburgerButton";
import { Logo } from "@silversea-ssc/ui-components-react";
import { legacyUrl } from "src/utils/routing/legacyUrl";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { setScrollOffset } from "src/utils/inPageNavigation";
import { useMenuSticky } from "components/StickyMenu/useSticky";
import { useIsDesktop } from "src/utils/useIsTabletOrDesktop";

const SecondaryMobileMenu = React.lazy(
  () => import("../SecondaryMobileMenu/SecondaryMobileMenu")
);

type Props = {
  fycUrl: string | undefined;
  searchUrl: string | undefined;
  topLinks: ValidatedTopMenuItem[];
  alternateLinks: AlternateLink[];
  data: DefaultNavBarQuery["mainMenu"];
  menuPrimaryCTA: CustomMenuItemFragment | undefined;
  variant?: "normal" | "desktop-compact";
  isTransparent?: boolean;
};

const MainMenu: React.FC<Props> = ({
  fycUrl,
  searchUrl,
  topLinks,
  alternateLinks,
  data,
  menuPrimaryCTA,
  variant = "normal",

  isTransparent = false
}) => {
  const language = useLanguage();
  const isDesktop = useIsDesktop();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const onToggleMenu = useCallback(() => {
    //recompute menu global css variables
    setScrollOffset();
    return setIsMenuOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    return globalHistory.listen(({ action }) => {
      if (action === "PUSH") setIsMenuOpen(false);
    });
  }, [setIsMenuOpen]);
  const sections = data?.mainMenu?.filter(isPresent) || [];
  //const primaryLinkProps = primaryLinkButtonProps(t, fycUrl, menuPrimaryCTA);
  const isHeaderSticky = useMenuSticky();

  return (
    <nav
      className={clsx(styles.header, {
        [styles.transparent]: isTransparent,
        [styles.transparentSticky]:
          isHeaderSticky && isTransparent && !isDesktop,
        [styles.open]: isMenuOpen,
        [styles.desktopCompact]: variant === "desktop-compact"
      })}
      data-content-name="main_menu"
    >
      <div className={styles.headerContainer}>
        <div className={styles.brandLogo}>
          <a
            href={legacyUrl(`/${language.path}`)}
            style={{ display: "block" }}
            data-content-piece="logo"
            title="Silversea"
          >
            <Logo color="currentColor" width={180} mobileWidth={145} />
          </a>
        </div>

        <ReactRemoveScroll
          enabled={isMenuOpen}
          className={clsx(styles.mainMenu)}
        >
          <div
            className={styles.scrollWrapper}
            data-content-name="primary_menu"
          >
            <ul
              className={styles.primaryNavContainer}
              role="menubar"
              aria-label="Primary menu"
              aria-orientation="horizontal"
            >
              {sections.map((item) => (
                <React.Fragment key={item._key}>
                  {arrayIsEmpty(item.items) ? (
                    <MenuSectionSingle
                      url={item.page?.fullPath}
                      label={
                        item.title?.localized ||
                        item.page?.card?.title?.localized ||
                        ""
                      }
                      trackingName={
                        item.trackingName || item.page?.metadata?.tracking?.name
                      }
                    />
                  ) : (
                    <MenuSection item={item} isMenuOpen={isMenuOpen} />
                  )}
                </React.Fragment>
              ))}

              {/* {primaryLinkProps && (
                <MenuItemCTA
                  className={styles.innerPrimaryNavCta}
                  {...primaryLinkProps}
                  label={primaryLinkProps.children}
                  variant="primary"
                />
              )} */}
            </ul>
            {isMenuOpen && (
              <React.Suspense>
                <SecondaryMobileMenu
                  alternateLinks={alternateLinks}
                  links={topLinks}
                  searchUrl={searchUrl}
                  className={styles.mobileSecondaryNavItems}
                />
              </React.Suspense>
            )}
          </div>
        </ReactRemoveScroll>
        {/* {primaryLinkProps && (
          <ul className={styles.primaryNavCta}>
            <MenuItemCTA
              {...primaryLinkProps}
              label={primaryLinkProps.children}
              variant="primary"
              size="extra-small"
            />
          </ul>
        )} */}
        <HamburgeButton
          isOpen={isMenuOpen}
          onToggleMenu={onToggleMenu}
          className={styles.hamburger}
        />
      </div>
    </nav>
  );
};

export default MainMenu;
